import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { clearAllCachesAndUnregister } from "@/lib/pwa";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = logged out
  const [booting, setBooting] = useState(true);

  const loadMe = useCallback(async () => {
    // The session_token HttpOnly cookie is sent automatically by the browser.
    // A 401 here means no valid session — treat as logged out.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setBooting(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  /**
   * Step 1 — password login.  Returns one of:
   *   { kind: "done",    user }                        // no MFA
   *   { kind: "mfa",     mfa_token, expires_in_seconds } // user has TOTP enabled
   */
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.requires_mfa) {
      return {
        kind: "mfa",
        mfa_token: data.mfa_token,
        expires_in_seconds: data.expires_in_seconds ?? 300,
      };
    }
    // Backend sets the HttpOnly session_token cookie on successful login.
    setUser(data.user);
    return { kind: "done", user: data.user };
  };

  /** Step 2 — submit the TOTP (or backup) code with the mfa_token. */
  const loginMfa = async (mfa_token, code) => {
    const { data } = await api.post("/auth/login/mfa", { mfa_token, code });
    // Backend sets the HttpOnly session_token cookie.
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    setUser(null);
    // Ask the backend to clear the session cookie, then wipe PWA caches.
    api.post("/auth/logout").catch(() => {});
    clearAllCachesAndUnregister().catch(() => {});
  };

  const can = (action) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (user.role === "coordinator") return action !== "manage_users" && action !== "import_xlsx";
    return false; // readonly
  };

  return (
    <AuthContext.Provider
      value={{ user, booting, login, loginMfa, logout, can, refresh: loadMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
